import { NOT_SET_LABEL } from "@/shared/domain/query/notSet";
import { type FractionDigits, NS_PER_S_BIGINT } from "@/shared/time";

import {
  boardInstantText,
  type BoardInstantForm,
  fewestFractionDigits,
} from "./boardTimeText";
import type {
  InteractionValue,
  InteractionValueType,
} from "./interactionTypes";
import {
  convertInteractionValue,
  instantNanoseconds,
  interactionValueKey,
  parseInteractionValue,
} from "./interactionValueCodec";

type Instant = Extract<InteractionValue, { type: "instant" }>;

/**
 * A value as a board writes it for a person to read. The control bar, the
 * Controls dialog, a panel title or chart text that reads a control, a
 * selection's clause, a chart tooltip, and the chat's context and
 * confirmation cards all write values with it, so one value reads the same in
 * each, except where a control's listed options give the value a label
 * (`listedLabelOf`).
 * - Null is "Not set" (`NOT_SET_LABEL`).
 * - An instant whose epoch is `unix` is written in `timeZone` as
 *   `unixInstantText` writes it: "January 4, 2026 (UTC)" for a midnight, and
 *   any other time with the seconds and digits below the second it needs,
 *   "January 4, 2026 at 2:05:30.25 PM (UTC)".
 * - An instant on any other epoch has no calendar to show, so it is its
 *   count of ticks, its unit and its epoch: "1500 ms (boot)".
 * - Any other value is written as it is.
 */
export function interactionValueText(
  value: InteractionValue,
  timeZone: string,
): string {
  switch (value.type) {
    case "null":
      return NOT_SET_LABEL;
    case "instant":
      return value.epoch === "unix"
        ? unixInstantText(value, timeZone)
        : `${value.ticks} ${value.unit} (${value.epoch})`;
    default:
      return String(value.value);
  }
}

/** An instant on the Unix timeline as `boardInstantText` writes it in
 *  `timeZone`, in the viewer's date and time format:
 *  "May 10, 2026 at 2:05 PM (UTC)", or its date alone for a midnight unless
 *  `form` asks for the time. */
export function unixInstantText(
  instant: Instant,
  timeZone: string,
  form?: BoardInstantForm,
): string {
  return boardInstantText(instant.ticks, instant.unit, timeZone, form);
}

/** The fewest digits below the second that write the instant exactly: none
 *  on a whole second, three at ".123", up to nine. */
export function exactFractionDigits(instant: Instant): FractionDigits {
  return fewestFractionDigits(
    ((instantNanoseconds(instant) % NS_PER_S_BIGINT) + NS_PER_S_BIGINT) %
      NS_PER_S_BIGINT,
  );
}

/** The label a value control's listed options (`options`, its `static`
 *  domain) give `value`, shown in place of the value's text; undefined when
 *  no option holds the value or the first option that holds it has no label
 *  (`listedOptionLabel`), and the value is then written as
 *  `interactionValueText` writes it. Each option's value is converted to the
 *  control's type `valueType` before it is compared with `value`, and an
 *  option that does not convert matches nothing. */
export function listedLabelOf(
  options: readonly { value: unknown; label: string }[],
  valueType: InteractionValueType,
  value: InteractionValue,
): string | undefined {
  const key = interactionValueKey(value);
  const entry = options.find((option) => {
    const parsed = parseInteractionValue(option.value);
    if (parsed.status !== "valid") {
      return false;
    }
    const converted = convertInteractionValue(parsed.value, valueType);
    return (
      converted.status === "valid" &&
      interactionValueKey(converted.value) === key
    );
  });
  return entry === undefined ? undefined : listedOptionLabel(entry);
}

/** A listed option's label; undefined when the label is empty or only
 *  spaces, which gives the option no label. */
export function listedOptionLabel(option: {
  label: string;
}): string | undefined {
  return option.label.trim() === "" ? undefined : option.label;
}
